import React, { useEffect, useState } from "react";
import { X, MapPin, Star, CheckCircle2 } from "lucide-react";

/**
 * Read-only course info panel opened from the "i" button on a golf club
 * card. Same panel component for both layouts — CSS breakpoints alone
 * decide the shape:
 *   - Mobile (default): bottom sheet, slides up from the bottom edge.
 *   - sm: and up: side panel, slides in from the right edge, pinned to
 *     the full viewport height (the desktop "screen opens on the side"
 *     behavior that was asked for).
 *
 * @param {{ course: any | null; onClose: () => void }} props
 */
export default function CourseInfoDrawer({ course, onClose }) {
  // Mount the backdrop/panel as soon as a course is set, then flip
  // `entered` on the next tick so the transform transition actually
  // animates in instead of snapping open.
  const [entered, setEntered] = useState(false);

  // Hero image swaps to whichever thumbnail was clicked. Reset back to
  // the course's main photo every time a new course is opened.
  const [activeImage, setActiveImage] = useState(course?.image ?? null);

  useEffect(() => {
    if (!course) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [course]);

  useEffect(() => {
    setActiveImage(course?.image ?? null);
  }, [course]);

  // Lock page scroll while the panel is open.
  useEffect(() => {
    if (!course) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [course]);

  if (!course) return null;

  // Main photo + gallery extras, de-duped and with blanks stripped.
  const gallery = Array.from(
    new Set([course.image, ...(course.images || [])].filter(Boolean)),
  );

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Panel — bottom sheet on mobile, right-side drawer from sm: up */}
      <div
        className={`fixed bg-white shadow-2xl flex flex-col
          inset-x-0 bottom-0 rounded-t-3xl max-h-[85vh]
          sm:inset-x-auto sm:right-0 sm:top-0 sm:bottom-0 sm:left-auto
          sm:w-full sm:max-w-md sm:max-h-none sm:rounded-none sm:rounded-l-3xl
          transition-transform duration-300 ease-out
          ${entered ? "translate-y-0 sm:translate-x-0" : "translate-y-full sm:translate-y-0 sm:translate-x-full"}
        `}
      >
        {/* Drag handle (mobile only) */}
        <div className="sm:hidden pt-2.5 pb-1 flex justify-center shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Hero image */}
          <div className="relative h-48 shrink-0">
            <img
              src={activeImage}
              alt={course.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-3 left-4 right-4 text-white">
              <h2 className="text-xl font-bold leading-snug">{course.name}</h2>
              <div className="flex items-center gap-3 mt-1 text-sm">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {course.city}{course.country ? `, ${course.country}` : ""}
                </span>
                {course.rating && (
                  <span className="flex items-center gap-1 font-semibold">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    {course.rating}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 -mt-1">
                {gallery.map((src, i) => (
                  <button
                    key={src + i}
                    type="button"
                    onClick={() => setActiveImage(src)}
                    aria-label={`Show photo ${i + 1}`}
                    className={`relative w-16 h-16 shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                      activeImage === src
                        ? "border-[#0B2421]"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {course.description && (
              <div>
                <h3 className="text-sm font-bold text-[#0B2421] uppercase tracking-wide mb-2">
                  About This Course
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{course.description}</p>
              </div>
            )}

            {course.difficulty != null && course.difficulty !== "" && (
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3">
                <h3 className="text-sm font-bold text-[#0B2421] uppercase tracking-wide mb-1.5">
                  Course Friendliness
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                  {course.difficulty}
                </p>
              </div>
            )}

            {course.fees_included && course.fees_included.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-[#0B2421] uppercase tracking-wide mb-2">
                  Included in Fees
                </h3>
                <div className="space-y-1.5">
                  {course.fees_included.map((fee, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      {fee}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {course.amenities && course.amenities.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-[#0B2421] uppercase tracking-wide mb-2">
                  Amenities & Features
                </h3>
                <div className="space-y-1.5">
                  {course.amenities.map((amenity, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      {amenity}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {course.price != null && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">From</p>
                <p className="text-[#0B2421] font-bold text-2xl">
                  SGD {course.price}
                  <span className="text-sm font-normal text-gray-400"> per player</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
